/**
 * B2B Gate Code Check
 * Handles Klaviyo form integration, business email validation, and access code verification.
 */
class B2BGate extends HTMLElement {
  #blockedDomains = [];
  #validCode = '';
  #redirectUrl = '/pages/b2b';
  #klaviyoSelector = '';
  #observer = null;
  #retryInterval = null;

  connectedCallback() {
    this.#blockedDomains = (this.dataset.blockedDomains || '').split(',').map(d => d.trim()).filter(Boolean);
    this.#validCode = this.dataset.validCode || '';
    this.#redirectUrl = this.dataset.redirectUrl || '/pages/b2b';
    this.#klaviyoSelector = this.dataset.klaviyoSelector || '';

    this.#setupUidRequired();
    this.#handleHashNavigation();
    this.#attachBusinessEmailCheck();

    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    if (this.#observer) this.#observer.disconnect();
    if (this.#retryInterval) clearInterval(this.#retryInterval);
    this.removeEventListener('click', this.#handleClick);
  }

  /** Set UID/VAT number field as required in Klaviyo form */
  #setupUidRequired() {
    if (!this.#klaviyoSelector) return;
    let tries = 0;
    const interval = setInterval(() => {
      const uidInput = this.querySelector(`${this.#klaviyoSelector} input[placeholder*="Nummer" i]`);
      if (uidInput) {
        uidInput.setAttribute('required', 'required');
        clearInterval(interval);
      }
      if (++tries >= 30) clearInterval(interval);
    }, 300);
  }

  /** If URL has #code-entry hash, show code entry section directly */
  #handleHashNavigation() {
    if (window.location.hash === '#code-entry') {
      const gate = this.querySelector('.b2b-gate');
      if (gate) gate.style.display = '';
      const banner = this.querySelector('#b2b-success-banner');
      if (banner) banner.style.display = 'none';
    }
  }

  /** Delegated click handler */
  #handleClick = (event) => {
    const target = event.target;
    if (!target) return;

    if (target.closest('[data-action="show-code-entry"]')) {
      this.#showCodeEntry();
    } else if (target.closest('[data-action="check-code"]')) {
      this.#checkCode();
    }
  };

  /** Show code entry section after successful email submission */
  #showCodeEntry() {
    const gate = this.querySelector('.b2b-gate');
    const banner = this.querySelector('#b2b-success-banner');
    if (banner) banner.style.display = 'none';
    if (gate) gate.style.display = '';

    const codeSection = this.querySelector('#b2b-code-section');
    if (codeSection) codeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });

    try { window.location.hash = 'code-entry'; } catch (_) {}
  }

  /** Validate and check the entered B2B access code */
  #checkCode() {
    const input = this.querySelector('#b2b-code');
    const error = this.querySelector('#b2b-code-error');
    if (!input) return;

    const entered = (input.value || '').trim();

    if (entered === this.#validCode) {
      if (error) error.style.display = 'none';
      try { localStorage.setItem('vail_b2b_access', 'granted'); } catch (_) {}
      window.location.href = this.#redirectUrl;
    } else {
      if (error) error.style.display = 'block';
      input.focus();
    }
  }

  /** Validate that an email is a business email (not on the blocked domains list) */
  #validateBusinessEmail(emailValue) {
    const value = (emailValue || '').trim().toLowerCase();
    const atIndex = value.lastIndexOf('@');
    if (atIndex === -1) {
      return { ok: false, msg: 'Bitte geben Sie eine gültige E-Mail Adresse ein.' };
    }

    const domain = value.slice(atIndex + 1);
    if (this.#blockedDomains.includes(domain) || !domain.includes('.')) {
      return { ok: false, msg: 'Bitte verwenden Sie eine geschäftliche E-Mail Adresse (keine Privatmail wie gmail.com).' };
    }
    return { ok: true };
  }

  /** Find Klaviyo success state nodes */
  #findSuccessNodes(container) {
    const nodes = [];
    const selectors = [
      '.ql-form-success',
      '.klaviyo-form-success',
      '.success_message',
      '[data-testid="form-success"]',
    ];
    for (const sel of selectors) {
      container.querySelectorAll(sel).forEach(n => nodes.push(n));
    }
    // Fallback: text search
    container.querySelectorAll('*').forEach(n => {
      if (n?.textContent && /thanks for subscribing/i.test(n.textContent.trim())) {
        nodes.push(n);
      }
    });
    return nodes;
  }

  /** Find the email input in a form */
  #getEmailInput(formEl) {
    return (
      formEl.querySelector('input[type="email"]') ||
      formEl.querySelector('input[name*="email" i]') ||
      formEl.querySelector('input[placeholder*="mail" i]')
    );
  }

  /** Attach business email validation to the Klaviyo form */
  #attachBusinessEmailCheck() {
    if (!this.#klaviyoSelector) return;

    const tryAttach = () => {
      const container = this.querySelector(this.#klaviyoSelector);
      if (!container) return false;

      const form = container.querySelector('form');
      if (!form) return false;

      const emailInput = this.#getEmailInput(form);
      if (!emailInput) return false;

      const uidInput =
        form.querySelector('input[placeholder*="UID" i]') ||
        form.querySelector('input[placeholder*="MWST" i]') ||
        form.querySelector('input[placeholder*="Nummer" i]');

      const errorEl = this.querySelector('#b2b-email-error');
      if (!errorEl) return false;

      if (form.dataset.vailEmailCheckAttached === 'true') return true;
      form.dataset.vailEmailCheckAttached = 'true';

      const successBanner = this.querySelector('#b2b-success-banner');

      const showError = (msg, fieldEl) => {
        const target = fieldEl || emailInput;
        errorEl.textContent = msg;
        errorEl.style.display = 'block';
        if (target) target.classList.add('b2b-input--error');
        if (target === emailInput && uidInput) uidInput.classList.remove('b2b-input--error');
        if (target === uidInput && emailInput) emailInput.classList.remove('b2b-input--error');
        container.dataset.vailBlocked = 'true';
        this.#findSuccessNodes(container).forEach(n => { n.style.display = 'none'; });
        if (form) form.style.display = '';
        if (successBanner) successBanner.style.display = 'none';
      };

      const clearError = (fieldEl) => {
        errorEl.style.display = 'none';
        if (fieldEl) fieldEl.classList.remove('b2b-input--error');
        if (emailInput && !fieldEl) emailInput.classList.remove('b2b-input--error');
        if (uidInput && !fieldEl) uidInput.classList.remove('b2b-input--error');
        delete container.dataset.vailBlocked;
      };

      // Live validation on blur
      emailInput.addEventListener('blur', () => {
        const res = this.#validateBusinessEmail(emailInput.value);
        if (!res.ok) showError(res.msg, emailInput);
        else clearError(emailInput);
      });

      if (uidInput) {
        uidInput.addEventListener('blur', () => {
          const v = (uidInput.value || '').trim();
          if (!v) {
            showError('Bitte geben Sie Ihre UID / MWST-Nummer ein.', uidInput);
          } else {
            clearError(uidInput);
          }
        });
      }

      // Block submit on invalid email (capture phase)
      form.addEventListener('submit', (e) => {
        if (emailInput) {
          const res = this.#validateBusinessEmail(emailInput.value);
          if (!res.ok) {
            showError(res.msg, emailInput);
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
        if (uidInput) {
          const v = (uidInput.value || '').trim();
          if (!v) {
            showError('Bitte geben Sie Ihre UID / MWST-Nummer ein.', uidInput);
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
        clearError();
      }, true);

      // Intercept Klaviyo button clicks that may not fire submit
      container.addEventListener('click', (e) => {
        const target = e.target;
        if (!target) return;
        if (
          target.matches('button[type="submit"], input[type="submit"], [role="button"], .kl-private-reset-css-Xuajs1 button') ||
          (target.textContent && /code anfordern|submit|anfordern/i.test(target.textContent))
        ) {
          if (emailInput) {
            const res = this.#validateBusinessEmail(emailInput.value);
            if (!res.ok) {
              showError(res.msg, emailInput);
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
          if (uidInput) {
            const v = (uidInput.value || '').trim();
            if (!v) {
              showError('Bitte geben Sie Ihre UID / MWST-Nummer ein.', uidInput);
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
          clearError();
        }
      }, true);

      // Observe mutations to handle Klaviyo success state
      this.#observer = new MutationObserver(() => {
        const blocked = container.dataset.vailBlocked === 'true';
        const successNodes = this.#findSuccessNodes(container);
        if (successNodes.length === 0) return;

        // Never show Klaviyo's native success
        successNodes.forEach(n => { n.style.display = 'none'; });

        if (blocked) {
          if (form) form.style.display = '';
          if (successBanner) successBanner.style.display = 'none';
          return;
        }

        // Valid business email → show custom banner, hide form
        if (successBanner) successBanner.style.display = 'block';
        const gateEl = this.querySelector('.b2b-gate');
        if (gateEl) gateEl.style.display = 'none';
        if (form) form.style.display = 'none';
      });

      this.#observer.observe(container, { childList: true, subtree: true });
      return true;
    };

    if (!tryAttach()) {
      let tries = 0;
      this.#retryInterval = setInterval(() => {
        if (tryAttach() || ++tries >= 30) clearInterval(this.#retryInterval);
      }, 400);
    }
  }
}

if (!customElements.get('b2b-gate')) {
  customElements.define('b2b-gate', B2BGate);
}
